import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Alert,
} from "@mui/material";
import { Business as BusinessIcon, Add as AddIcon } from "@mui/icons-material";
import api from "../../services/api";

/**
 * ClienteSelect - Componente Autocomplete para seleccionar clientes
 * CON OPCIÓN para crear clientes nuevos directamente
 */
const ClienteSelect = ({
  value,
  onChange,
  error,
  helperText,
  disabled = false,
}) => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [selectedCliente, setSelectedCliente] = useState(null);

  // Dialog estados
  const [openDialog, setOpenDialog] = useState(false);
  const [creatingCliente, setCreatingCliente] = useState(false);
  const [dialogError, setDialogError] = useState("");
  const [newClienteData, setNewClienteData] = useState({
    nombre: "",
    telefono: "",
    direccion: "",
    canal: "pagina web",
    cliente_de: "",
  });

  // Cargar clientes al montar
  useEffect(() => {
    loadClientes();
  }, []);

  // Actualizar selected cuando cambia value (prop)
  useEffect(() => {
    if (value && clientes.length > 0) {
      const found = clientes.find((c) => c.id === value);
      setSelectedCliente(found || null);
    }
  }, [value, clientes]);

  const loadClientes = async () => {
    try {
      setLoading(true);
      const response = await api.get("/clientes");
      const lista =
        response.data?.clientes ||
        (Array.isArray(response.data) ? response.data : []);
      setClientes(Array.isArray(lista) ? lista : []);
    } catch (error) {
      setClientes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (searchValue) => {
    setInputValue(searchValue);
    if (searchValue.length < 2) {
      loadClientes();
      return;
    }

    try {
      setLoading(true);
      const response = await api.get("/clientes", {
        params: { search: searchValue },
      });
      const lista =
        response.data?.clientes ||
        (Array.isArray(response.data) ? response.data : []);
      setClientes(Array.isArray(lista) ? lista : []);
    } catch (error) {
      // Error silencioso
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event, newValue) => {
    setSelectedCliente(newValue);
    onChange(newValue?.id || null);
  };

  // ============================================================================
  // CREAR CLIENTE - Dialog
  // ============================================================================
  const handleOpenDialog = () => {
    setOpenDialog(true);
    setDialogError("");
    setNewClienteData({
      nombre: "",
      telefono: "",
      direccion: "",
      canal: "pagina web",
      cliente_de: "",
    });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogError("");
  };

  const handleCreateCliente = async () => {
    // Validar nombre requerido
    if (!newClienteData.nombre.trim()) {
      setDialogError("El nombre del cliente es requerido");
      return;
    }

    try {
      setCreatingCliente(true);
      setDialogError("");

      // Crear cliente
      const response = await api.post("/clientes", newClienteData);
      const newId = response.data?.id;

      // Recargar lista completa y seleccionar el nuevo cliente
      const listResponse = await api.get("/clientes");
      const listaActualizada =
        listResponse.data?.clientes ||
        (Array.isArray(listResponse.data) ? listResponse.data : []);
      const listaFinal = Array.isArray(listaActualizada)
        ? listaActualizada
        : [];
      setClientes(listaFinal);

      // Seleccionar el cliente recién creado
      if (newId) {
        const clienteCreado = listaFinal.find((c) => c.id === newId) || {
          id: newId,
          nombre: newClienteData.nombre,
        };
        setSelectedCliente(clienteCreado);
        onChange(newId);
      }

      // Cerrar dialog
      handleCloseDialog();
    } catch (err) {
      const msgError =
        err.response?.data?.message ||
        err.message ||
        "Error al crear el cliente";
      setDialogError(msgError);
    } finally {
      setCreatingCliente(false);
    }
  };

  return (
    <Box>
      {/* Autocomplete para seleccionar cliente */}
      <Autocomplete
        fullWidth
        disabled={disabled || loading}
        options={clientes}
        getOptionLabel={(option) => option?.nombre || ""}
        value={selectedCliente}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={(event, value, reason) => {
          if (reason === "input" || reason === "reset") {
            handleSearch(value);
          }
        }}
        loading={loading}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Cliente"
            placeholder="Buscar cliente..."
            error={!!error}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box {...props} component="li">
            <BusinessIcon sx={{ mr: 1, fontSize: 18 }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {option.nombre}
              </Typography>
              {option.telefono && (
                <Typography variant="caption" color="textSecondary">
                  📱 {option.telefono}
                </Typography>
              )}
              {option.direccion && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                >
                  📍 {option.direccion}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        noOptionsText="No se encontraron clientes"
      />

      {/* Botón para crear cliente */}
      <Button
        fullWidth
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleOpenDialog}
        sx={{ mt: 1 }}
        size="small"
      >
        Crear Nuevo Cliente
      </Button>

      {/* Dialog para crear cliente */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Crear Nuevo Cliente</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Nombre *"
            placeholder="Nombre del cliente"
            value={newClienteData.nombre}
            onChange={(e) =>
              setNewClienteData({ ...newClienteData, nombre: e.target.value })
            }
            margin="normal"
            disabled={creatingCliente}
          />

          <TextField
            fullWidth
            label="Teléfono"
            placeholder="10 dígitos"
            value={newClienteData.telefono}
            onChange={(e) =>
              setNewClienteData({
                ...newClienteData,
                telefono: e.target.value.replace(/\D/g, "").slice(0, 10),
              })
            }
            margin="normal"
            disabled={creatingCliente}
            inputProps={{ maxLength: 10, inputMode: "numeric" }}
            helperText={
              newClienteData.telefono && newClienteData.telefono.length < 10
                ? `${newClienteData.telefono.length}/10 dígitos`
                : ""
            }
          />

          <TextField
            fullWidth
            label="Canal"
            select
            value={newClienteData.canal}
            onChange={(e) =>
              setNewClienteData({ ...newClienteData, canal: e.target.value })
            }
            margin="normal"
            disabled={creatingCliente}
          >
            <MenuItem value="redes sociales">Redes Sociales</MenuItem>
            <MenuItem value="familiares">Familiares</MenuItem>
            <MenuItem value="referencias">Referencias</MenuItem>
            <MenuItem value="pagina web">Página Web</MenuItem>
          </TextField>

          <TextField
            fullWidth
            label="Dirección"
            placeholder="Calle, número y ciudad"
            value={newClienteData.direccion}
            onChange={(e) =>
              setNewClienteData({
                ...newClienteData,
                direccion: e.target.value,
              })
            }
            margin="normal"
            disabled={creatingCliente}
            multiline
            rows={2}
          />

          <TextField
            fullWidth
            label="Cliente De"
            placeholder="Empresa o contacto"
            value={newClienteData.cliente_de}
            onChange={(e) =>
              setNewClienteData({
                ...newClienteData,
                cliente_de: e.target.value,
              })
            }
            margin="normal"
            disabled={creatingCliente}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} disabled={creatingCliente}>
            Cancelar
          </Button>
          <Button
            onClick={handleCreateCliente}
            variant="contained"
            disabled={creatingCliente}
          >
            {creatingCliente ? "Creando..." : "Crear Cliente"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClienteSelect;
